import classNames from "classnames";
import clamp from "lodash/clamp";
import find from "lodash/find";
import floor from "lodash/floor";
import map from "lodash/map";
import times from "lodash/times";
import React, { FunctionComponent, useMemo } from "react";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Length, Unit } from "../../../../types/customization/box";
import {
  TemplateElementDefinition,
  TemplatePageDefinition,
} from "../../../../types/customization/template";
import {
  TextAlignHorizontal,
  TextAlignVertical,
  TextOrientation,
  UnsupportedTextAlignError,
  UnsupportedTextOrientationError,
} from "../../../../types/customization/text";
import { imageUrlFromAssetDefinition } from "../../../../utils/customization/asset";
import {
  calcAbsolutePosition,
  positionDmmToMm,
  sizeDmmToMm,
} from "../../../../utils/customization/box";
import { findAssetDefinition, findPageDefinition } from "../../../../utils/customization/finders";
import { sortElementsForRendering } from "../../../../utils/customization/page";
import {
  calcTextProperties,
  textOrientationToRotation,
} from "../../../../utils/customization/text";
import ClippingPath from "../ClippingPath";
import "./style.scss";

interface Props {
  pageDefinitionKey: string;
  restrictedSize?: number;
  selected: boolean;
}

const BACKGROUND_COLOR = "#5e646e";
const TEXT_COLOR = "#0064f6";

const MockPage: FunctionComponent<Props> = ({ pageDefinitionKey, restrictedSize, selected }) => {
  const { template } = useCustomizationContext();

  const pageDefinition = findPageDefinition(pageDefinitionKey, template);

  const elements = map(
    sortElementsForRendering(pageDefinition.elementDefinitions),
    (elementDefinition) => (
      <MockElement
        elementDefinition={elementDefinition}
        key={elementDefinition.key}
        pageDefinition={pageDefinition}
      />
    )
  );

  const boxSize = useMemo(() => {
    if (!restrictedSize) {
      return { height: undefined, width: undefined };
    }
    if (pageDefinition.height.value > pageDefinition.width.value) {
      return {
        height: restrictedSize,
        width: (pageDefinition.width.value / pageDefinition.height.value) * restrictedSize,
      };
    } else if (pageDefinition.width.value > pageDefinition.height.value) {
      return {
        height: (pageDefinition.height.value / pageDefinition.width.value) * restrictedSize,
        width: restrictedSize,
      };
    }
    return { height: restrictedSize, width: restrictedSize };
  }, [pageDefinition.height.value, pageDefinition.width.value, restrictedSize]);

  const mockSvg = (
    <svg
      className={classNames("MockPage", { "MockPage--selected": selected })}
      height={boxSize.height}
      preserveAspectRatio="xMidYMid"
      viewBox={`0 0 ${pageDefinition.width.value} ${pageDefinition.height.value}`}
      width={boxSize.width}
    >
      {elements}
    </svg>
  );

  const shapeDefinition = find(
    template.shapeDefinitions,
    ({ key }) => key === pageDefinition.shapeKey
  );
  let clippedSVG;
  const clippingSVG = shapeDefinition ? shapeDefinition.svgContent : null;
  if (clippingSVG) {
    const clippingId = `clippingMock-${pageDefinition.shapeKey || pageDefinitionKey}-${uuid()}`;
    clippedSVG = (
      <>
        <div style={{ height: 0, overflow: "hidden", width: 0 }}>
          {clippingSVG && <ClippingPath contentSVG={clippingSVG} id={clippingId} />}
        </div>
        <div
          style={{
            WebkitClipPath: `url(#${clippingId})`,
            clipPath: `url(#${clippingId})`,
          }}
        >
          {mockSvg}
        </div>
      </>
    );
  }

  return <>{clippedSVG ? clippedSVG : mockSvg}</>;
};

interface MockElementProps {
  elementDefinition: TemplateElementDefinition;
  pageDefinition: TemplatePageDefinition;
}

const MockElement: FunctionComponent<MockElementProps> = ({
  elementDefinition,
  pageDefinition,
}) => {
  const { configuration, template } = useCustomizationContext();

  const { anchorPage, anchorSelf } = elementDefinition;
  const elementSizeMm = sizeDmmToMm(elementDefinition);

  const absolutePosition = calcAbsolutePosition<Unit.MM>({
    elementAnchor: anchorSelf,
    elementPosition: positionDmmToMm(elementDefinition),
    elementSize: elementSizeMm,
    pageAnchor: anchorPage,
    pageSize: pageDefinition,
  });

  switch (elementDefinition.type) {
    case "date-cell":
    case "editable-picture": {
      let maskSVGElement: React.ReactElement<React.SVGProps<SVGMaskElement>> | undefined;
      if (elementDefinition.type === "editable-picture" && elementDefinition.shapeKey) {
        const shapeDefinition = find(
          template.shapeDefinitions,
          ({ key }) => key === elementDefinition.shapeKey
        );
        if (!shapeDefinition) {
          throw new Error("shapeDefinition not found");
        }

        maskSVGElement = (
          <mask id={uuid()} maskContentUnits="userSpaceOnUse" maskUnits="userSpaceOnUse">
            <g
              dangerouslySetInnerHTML={{ __html: shapeDefinition.svgContent }}
              height="100%"
              width="100%"
              x="0"
              y="0"
            />
          </mask>
        );
      }

      return (
        <svg
          height={elementSizeMm.height.value}
          viewBox={`0 0 ${elementSizeMm.width.value} ${elementSizeMm.height.value}`}
          width={elementSizeMm.width.value}
          x={absolutePosition.x.value}
          y={absolutePosition.y.value}
        >
          <defs>{maskSVGElement}</defs>
          <g mask={maskSVGElement?.props?.id ? `url(#${maskSVGElement.props.id})` : undefined}>
            <rect
              className="MockPage-element MockPage-element--editablePicture"
              fill={BACKGROUND_COLOR}
              height="100%"
              strokeWidth="0"
              width="100%"
              x="0"
              y="0"
            />
          </g>
        </svg>
      );
    }
    case "date-text":
    case "editable-text":
    case "forced-text": {
      const { fontSizeLength, lineHeightLength, maxLines } = calcTextProperties(
        elementDefinition,
        template.fontDefinitions[0]
      );

      const lines = clamp(floor((maxLines * 2) / 3), maxLines >= 2 ? 2 : 1, maxLines);

      const rotation = textOrientationToRotation(elementDefinition.textOrientation);
      const rotationLeft = absolutePosition.x.value + elementSizeMm.width.value / 2;
      const rotationTop = absolutePosition.y.value + elementSizeMm.height.value / 2;
      const transform = `rotate(${rotation} ${rotationLeft} ${rotationTop})`;

      let orientedWidth: Length<Unit.MM>;
      let orientedHeight: Length<Unit.MM>;
      switch (elementDefinition.textOrientation) {
        case TextOrientation.O000:
        case TextOrientation.O180:
          orientedWidth = elementSizeMm.width;
          orientedHeight = elementSizeMm.height;
          break;
        case TextOrientation.O090:
        case TextOrientation.O270:
          orientedWidth = elementSizeMm.height;
          orientedHeight = elementSizeMm.width;
          break;
        default: {
          const exhaustiveCheck1: never = elementDefinition.textOrientation;
          throw new UnsupportedTextOrientationError(exhaustiveCheck1);
        }
      }

      let offsetY: number;
      switch (elementDefinition.textAlign[0]) {
        case TextAlignVertical.TOP:
          offsetY = 0;
          break;
        case TextAlignVertical.MIDDLE:
          offsetY = (orientedHeight.value - lines * lineHeightLength.value) / 2;
          break;
        case TextAlignVertical.BOTTOM:
          offsetY = orientedHeight.value - lines * lineHeightLength.value;
          break;
        default: {
          const exhaustiveCheck2: never = elementDefinition.textAlign[0];
          throw new UnsupportedTextAlignError(exhaustiveCheck2);
        }
      }

      return (
        <g transform={transform}>
          <svg
            height={orientedHeight.value}
            viewBox={`0 0 ${orientedWidth.value} ${orientedHeight.value}`}
            width={orientedWidth.value}
            x={absolutePosition.x.value + (elementSizeMm.width.value - orientedWidth.value) / 2}
            y={absolutePosition.y.value + (elementSizeMm.height.value - orientedHeight.value) / 2}
          >
            {times(lines, (i) => {
              const relLineWidth = i % 2 ? 0.6 : 0.5;
              const width = elementSizeMm.width.value * relLineWidth;
              let x: number;

              switch (elementDefinition.textAlign[1]) {
                case TextAlignHorizontal.LEFT:
                  x = 0;
                  break;
                case TextAlignHorizontal.CENTER:
                  x = (orientedWidth.value - width) / 2;
                  break;
                case TextAlignHorizontal.RIGHT:
                  x = orientedWidth.value - width;
                  break;
                default: {
                  const exhaustiveCheck3: never = elementDefinition.textAlign[1];
                  throw new UnsupportedTextAlignError(exhaustiveCheck3);
                }
              }

              const y =
                offsetY +
                i * lineHeightLength.value +
                (lineHeightLength.value - fontSizeLength.value) / 2;

              return (
                <rect
                  className="MockPage-element MockPage-element--editableText"
                  fill={TEXT_COLOR}
                  height={fontSizeLength.value}
                  key={i}
                  strokeWidth="0"
                  width={width}
                  x={x}
                  y={y}
                />
              );
            })}
          </svg>
        </g>
      );
    }
    case "forced-image": {
      const assetDefinition = findAssetDefinition(elementDefinition.assetKey, template);
      const assetUrlTemplate = configuration && configuration.assetUrlTemplate;
      const imageUrl = imageUrlFromAssetDefinition(assetDefinition, 60, assetUrlTemplate);

      return (
        <image
          className="MockPage-element MockPage-element--forcedImage"
          height={elementSizeMm.height.value}
          width={elementSizeMm.width.value}
          x={absolutePosition.x.value}
          xlinkHref={imageUrl}
          y={absolutePosition.y.value}
        />
      );
    }
    default: {
      const exhaustiveCheck: never = elementDefinition;
      throw new Error(
        `Unsupported elementDefinition type "${(exhaustiveCheck as { type: string }).type}"`
      );
    }
  }
};

export default MockPage;
